import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Select from "antd/es/select";
import { useIntl } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FunnelStageValue from "@mapmycustomers/shared/types/FunnelStageValue";
import { Labeled } from "@mapmycustomers/ui";

import EntityBadge from "@app/component/EntityBadge";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { ensureStagesFetched } from "@app/store/deal/actions";
import { RootState } from "@app/store/rootReducer";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";

import styles from "./FunnelField.module.scss";

interface Props {
  disabled?: boolean;
  ensureStagesFetched: typeof ensureStagesFetched.request;
  funnels: Funnel[];
  funnelStages: Record<Funnel["id"], Stage[]>;
  isStageRequired?: boolean;
  onChange: (value: FunnelStageValue) => void;
  value?: Partial<FunnelStageValue>;
}

const FunnelField: React.FC<Props> = ({
  disabled,
  ensureStagesFetched,
  funnels,
  funnelStages,
  isStageRequired = true,
  onChange,
  value,
}) => {
  const intl = useIntl();
  const [internalValue, setInternalValue] = useState<Partial<FunnelStageValue> | undefined>(
    value ?? {
      funnel: funnels.length ? { id: funnels[0].id } : undefined,
      stage: undefined,
    }
  );

  useEffect(() => {
    if (internalValue?.funnel) {
      ensureStagesFetched([internalValue.funnel.id]);
    }
  }, [ensureStagesFetched, internalValue]);

  useEffect(() => {
    if (isStageRequired) {
      if (internalValue?.funnel && internalValue?.stage) {
        onChange(internalValue as FunnelStageValue);
      }
    } else if (internalValue?.funnel || internalValue?.stage) {
      onChange(internalValue as FunnelStageValue);
    }
  }, [isStageRequired, onChange, internalValue]);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const funnelOptions = useMemo(() => funnels.map(defaultSelectOptionMapper), [funnels]);
  const stagesOptions = useMemo(
    () => (internalValue?.funnel ? funnelStages[internalValue.funnel.id] : []),
    [funnelStages, internalValue]
  );

  const handleFunnelChange = useCallback(
    (funnelId: Funnel["id"]) =>
      setInternalValue((value) => ({
        ...value,
        funnel: { id: funnelId },
        stage: undefined,
      })),
    []
  );

  const handleStageChange = useCallback(
    (stageId: Stage["id"]) =>
      setInternalValue((internalValue) => ({ ...internalValue, stage: { id: stageId } })),
    []
  );

  return (
    <div className={styles.container}>
      <Labeled
        label={intl.formatMessage({
          id: "view.selectBar.button.bulkEdit.panel.changeFunnel.label",
          defaultMessage: "Change Funnel to",
          description: "Bulk Edit funnel control label",
        })}
      >
        <Select<Funnel["id"]>
          className={styles.fullWidth}
          disabled={disabled}
          dropdownMatchSelectWidth={false}
          onChange={handleFunnelChange}
          options={funnelOptions}
          placeholder={intl.formatMessage({
            id: "view.selectBar.button.bulkEdit.panel.changeFunnel.placeholder",
            defaultMessage: "Select funnel",
            description: "Select funnel placeholder",
          })}
          value={internalValue?.funnel?.id}
        />
      </Labeled>

      <Labeled
        label={intl.formatMessage({
          id: "view.selectBar.button.bulkEdit.panel.changeStage.label",
          defaultMessage: "Change Stage to",
          description: "Bulk Edit stage control label",
        })}
      >
        <Select<Stage["id"]>
          className={styles.fullWidth}
          disabled={disabled}
          dropdownMatchSelectWidth={false}
          onChange={handleStageChange}
          placeholder={intl.formatMessage({
            id: "view.selectBar.button.bulkEdit.panel.changeStage.placeholder",
            defaultMessage: "Select stage",
            description: "Select stage placeholder",
          })}
          value={internalValue?.stage?.id}
        >
          {stagesOptions.map((stage) => (
            <Select.Option key={stage.id} value={stage.id}>
              <EntityBadge entity={stage} />
            </Select.Option>
          ))}
        </Select>
      </Labeled>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
});

const mapDispatchToProps = {
  ensureStagesFetched: ensureStagesFetched.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(FunnelField);
